import { ReactElement, useState, useMemo, useCallback } from 'react';

import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { clearSelectionAndSelectMulti, selectSimilarUrls, selectSelectedUrls, setNotification } from '../../store/uiSlice';
import { selectWindowsSortedExcept } from '../../store/selectors';
import { selectTabs, selectDuplicateUrls, dedupTabsAsync, moved, Tab } from '../../store/windowsSlice';

import WindowTabList from './WindowTabList';
import { useLiveQuery } from 'dexie-react-hooks';
import { getTabvanaWindowId } from '../../db/prefs';
import FlexViewHeader from '../flexView/FlexViewHeader';
import Inspector from '../inspector/Inspector';
import { RootState } from '../../store/rootState';
import SplitPane from 'react-split-pane';
import { Page, PageFilter, PageSortOrder, fetchPagesByNormalizedUrls, fetchPages } from '../../db/pages';
import { sortBy } from 'lodash';
import { pageSortOrderMap, filterPage } from '../sort';
import PageRow from '../flexView/pagelists/pagerow/PageRow';
import { shouldIgnore } from '../../db/urlNormalizationPrefs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClone } from '@fortawesome/free-solid-svg-icons';
import IconButton from '@mui/joy/IconButton';
import { Tooltip } from '@mui/joy';
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { logger } from '../../utils/logger';

type LocalItems = Record<string, number[]>;

function syntheticPageFromTab(tab: Tab): Page {
  return {
    nurl: tab.url!,
    title: tab.title,
    visible: 'active',
    accessTimes: [],
    lastActiveDesc: 0,
    hotnessDesc: 0,
    humanTags: [],
    humanRejectedTags: [],
    autoTags: [],
    openerUrls: [],
    openedUrls: [],
    timeAdjacentUrls: [],
  };
}

const Windows = (): ReactElement => {
  const [filter, setFilter] = useState({ flagged: false });
  const [groupByWindow, setGroupByWindow] = useState(true);
  const [sortOrder, setSortOrder] = useState(PageSortOrder.recent);
  const [activeDragId, setActiveDragId] = useState<number | null>(null);
  const [localItems, setLocalItems] = useState<LocalItems | null>(null);

  const tabvanaWindowId = useLiveQuery(() => {
    return getTabvanaWindowId();
  }, []);

  const windowsSorted = useAppSelector((state: RootState) =>
    selectWindowsSortedExcept(state, tabvanaWindowId)
  );
  const tabs = useAppSelector(selectTabs);
  const dispatch = useAppDispatch();
  const duplicateUrls = useAppSelector(selectDuplicateUrls);
  const similarUrls = useAppSelector(selectSimilarUrls);
  const selectedUrls = useAppSelector(selectSelectedUrls);

  // Data for Flat View and DragOverlay
  const allTabIds = useMemo(() => {
     if (!windowsSorted) return [];
     // Deduplicate: a tab can transiently appear in two windows if attached fires before detached.
     return [...new Set(windowsSorted.flatMap(([_, w]) => w.tabIds))];
  }, [windowsSorted]);

  const allTabObjects = useMemo(() => allTabIds.map(id => tabs[id]).filter(t => t && t.url), [allTabIds, tabs]);
  const allUrls = useMemo(() => allTabObjects.map(t => t.url!.normalized), [allTabObjects]);

  const pages = useLiveQuery(() => fetchPages(allUrls), [allUrls]);

  // Build a map for quick page lookup (used by DragOverlay)
  const pageByNurl = useMemo(
    () => pages ? new Map(pages.map(p => [p.nurl.normalized, p])) : new Map<string, Page>(),
    [pages]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const findWindowForTab = useCallback((tabId: number, items: LocalItems): string | undefined => {
    for (const [wid, ids] of Object.entries(items)) {
      if (ids.includes(tabId)) return wid;
    }
    return undefined;
  }, []);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const tabId = event.active.id as number;
    setActiveDragId(tabId);
    if (!windowsSorted) return;
    const initial: LocalItems = {};
    for (const [windowId, w] of windowsSorted) {
      initial[String(windowId)] = [...w.tabIds];
    }
    setLocalItems(initial);
  }, [windowsSorted]);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    const { active, over } = event;
    if (!over || !localItems) return;
    const activeId = active.id as number;
    const overId = over.id as number;
    if (activeId === overId) return;

    const activeWindow = findWindowForTab(activeId, localItems);
    const overWindow = findWindowForTab(overId, localItems);
    if (!activeWindow || !overWindow) return;

    setLocalItems(prev => {
      if (!prev) return prev;
      if (activeWindow === overWindow) {
        const items = [...prev[activeWindow]];
        const oldIdx = items.indexOf(activeId);
        const newIdx = items.indexOf(overId);
        if (oldIdx === newIdx) return prev;
        return { ...prev, [activeWindow]: arrayMove(items, oldIdx, newIdx) };
      } else {
        const sourceItems = prev[activeWindow].filter(id => id !== activeId);
        const targetItems = [...prev[overWindow]];
        const overIdx = targetItems.indexOf(overId);
        targetItems.splice(overIdx >= 0 ? overIdx : targetItems.length, 0, activeId);
        return { ...prev, [activeWindow]: sourceItems, [overWindow]: targetItems };
      }
    });
  }, [localItems, findWindowForTab]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const activeId = event.active.id as number;
    setActiveDragId(null);

    if (!localItems) return;

    const targetWindowStr = findWindowForTab(activeId, localItems);
    if (!targetWindowStr) {
      setLocalItems(null);
      return;
    }
    const targetWindowId = Number(targetWindowStr);
    const targetIndex = localItems[targetWindowStr].indexOf(activeId);
    const tab = tabs[activeId];
    const sourceWindowId = tab?.windowId;

    if (tab && targetIndex !== -1) {
      if (sourceWindowId === targetWindowId) {
        // Optimistic Redux update for same-window reorder
        dispatch(moved({ tabId: activeId, windowId: targetWindowId, fromIndex: 0, toIndex: targetIndex }));
      }
      // Actually move in Chrome (handles both same-window and cross-window)
      chrome.tabs.move(activeId, { windowId: targetWindowId, index: targetIndex }).catch(e => {
        logger.error('[DnD] chrome.tabs.move failed:', e);
      });
    }

    // Keep localItems briefly so UI stays consistent while Chrome events arrive
    setTimeout(() => setLocalItems(null), 400);
  }, [localItems, tabs, dispatch, findWindowForTab]);

  const handleDragCancel = useCallback(() => {
    setActiveDragId(null);
    setLocalItems(null);
  }, []);

  // Active drag page for DragOverlay
  const activeDragTab = activeDragId !== null ? tabs[activeDragId] : null;
  const activeDragPage = activeDragTab?.url ? pageByNurl.get(activeDragTab.url.normalized) : undefined;

  let content;
  if (!windowsSorted) {
      content = <></>;
  } else if (groupByWindow) {
      const isDragging = activeDragId !== null;
      content = windowsSorted.map(([windowId, window]) => {
        const dragIds = localItems ? (localItems[String(windowId)] ?? window.tabIds) : window.tabIds;
        return (
          <WindowTabList
            key={windowId}
            windowId={Number(windowId)}
            filter={filter}
            sortOrder={sortOrder}
            dragItemIds={dragIds}
            isDragEnabled={true}
            activeDragId={activeDragId}
          />
        );
      });
  } else {
      if (!pages) {
          content = <></>;
      } else {
          const pageMap = new Map(pages.map(p => [p.nurl.normalized, p]));

          const enrichedTabs = allTabObjects.map(tab => ({
              tab,
              page: pageMap.get(tab.url!.normalized),
          }));

          const filteredItems = enrichedTabs.filter(item =>
            item.page
              ? filterPage(item.page, filter)
              : !filter.flagged && shouldIgnore(item.tab.url!.normalized)
          );

          let sortedItems = filteredItems;

          if (sortOrder === PageSortOrder.similarity) {
              sortedItems = sortBy(filteredItems, [
                  (item) => !item.page || !selectedUrls.includes(item.page.nurl.normalized),
                  (item) => -(item.page ? similarUrls[item.page.nurl.normalized] || 0 : 0),
              ]);
          } else if (sortOrder !== PageSortOrder.recent) {
              sortedItems = sortBy(filteredItems, (item) =>
                item.page ? pageSortOrderMap[sortOrder](item.page) : 0
              );
          }

          content = (
              <div
                style={{
                    border: '1px solid',
                    margin: '0.5rem 0.5rem 0.5rem 0.5rem',
                    borderColor: 'rgb(50, 56, 62)',
                }}
              >
                {sortedItems.map(item =>
                    item.page
                      ? <PageRow
                            key={item.tab.id}
                            page={item.page}
                            windowId={item.tab.windowId}
                            tabId={item.tab.id}
                        />
                      : <PageRow
                            key={item.tab.id}
                            page={syntheticPageFromTab(item.tab)}
                            windowId={item.tab.windowId}
                            tabId={item.tab.id}
                            ignoredMode
                        />
                )}
            </div>
          );
      }
  }

  const dedupButton = duplicateUrls.length > 0 && (
    <Tooltip title="Deduplicate" placement="bottom">
      <IconButton
        size="sm"
        variant="soft"
        color="danger"
        onClick={() => {
          void dispatch(dedupTabsAsync()).then((result) => {
            if (result.meta.requestStatus === 'fulfilled') {
              const skipped = (result.payload as { skipped: number }).skipped;
              if (skipped > 0) {
                dispatch(setNotification(`${skipped} tab${skipped === 1 ? '' : 's'} not closed — unsaved changes`));
              }
            }
          });
        }}
        title="Deduplicate"
      >
        <FontAwesomeIcon icon={faClone} />
        <span style={{ marginLeft: 5 }}>{duplicateUrls.length}</span>
      </IconButton>
    </Tooltip>
  );

  const windowsContent = groupByWindow ? (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      {content}
      <DragOverlay>
        {activeDragId !== null && activeDragPage && activeDragTab ? (
          <div style={{ background: 'rgb(50, 56, 62)', borderRadius: 4, opacity: 0.9 }}>
            <PageRow
              page={activeDragPage}
              windowId={activeDragTab.windowId}
              tabId={activeDragId}
            />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  ) : content;

  return (
    /* this lib is incompatible with react18. To fix
     children: React.ReactNode; needs to be added to SplitPaneProps.
     @ts-ignore TS2322 */
    <SplitPane
      allowResize={true}
      split="vertical"
      defaultSize={300}
      minSize={10}
      primary="second"
    >
      <div className="fill flexContainerHorizontal">
        <div
          className="flexFillHorizontal"
          style={{
            overflow: 'scroll',
          }}
        >
          {windowsContent}
        </div>

        <div
          style={{
            borderLeft: '1px solid',
            borderColor: 'rgb(50, 56, 62)',
            height: '100%',
          }}
        ></div>
      </div>
      <Inspector
        onSelectAll={() => {
          if (!windowsSorted) return;

          const allTabsInWindows = windowsSorted
            .flatMap(([, window]) => window.tabIds)
            .map((tabId) => tabs[tabId])
            .filter((tab) => tab != null && tab.url != null);

          let finalTabs = allTabsInWindows;

          if (pages && filter.flagged) {
             const pageMap = new Map(pages.map(p => [p.nurl.normalized, p]));
             finalTabs = allTabsInWindows.filter(t => {
                 const p = pageMap.get(t.url!.normalized);
                 return p && filterPage(p, filter);
             });
          }

          dispatch(
            clearSelectionAndSelectMulti({
              urls: finalTabs.map((t) => ({ normalized: t.url!.normalized } as any)),
            })
          );
        }}
      >
        <FlexViewHeader
          tagsSorted={[]}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
          filter={filter}
          setFilter={setFilter}
          groupByWindow={groupByWindow}
          setGroupByWindow={setGroupByWindow}
        >
            {dedupButton}
        </FlexViewHeader>
      </Inspector>
    </SplitPane>
  );
};

export default Windows;
